import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Modal from './Modal';
import { usePQC } from '../hooks/usePQC';
import { useAuth } from '../contexts/AuthContext';
import { 
  Key, 
  Shield, 
  RefreshCw, 
  Download, 
  Upload, 
  Eye, 
  EyeOff, 
  Copy, 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Clock, 
  Zap,
  Lock,
  Unlock,
  FileKey,
  Settings,
  Info,
  Trash2,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface PQCKeyManagerProps {
  className?: string;
}

const PQCKeyManager: React.FC<PQCKeyManagerProps> = ({ className }) => {
  const { user } = useAuth();
  const {
    isInitialized,
    kyberKeys,
    dilithiumKeys,
    securityStatus,
    securityLogs,
    isGeneratingKeys,
    isBackingUp,
    isRestoring,
    generateKeys,
    backupKeys,
    restoreKeys,
    rotateKeys,
    removeKeys,
    getKeyMetrics,
    validateKeyIntegrity,
    hasValidKeys
  } = usePQC();

  const [showPrivateKeys, setShowPrivateKeys] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showKeyDetails, setShowKeyDetails] = useState(false);
  const [backupData, setBackupData] = useState('');
  const [restoreData, setRestoreData] = useState('');
  const [selectedAlgorithms, setSelectedAlgorithms] = useState({
    kyber: 'Kyber-768' as 'Kyber-512' | 'Kyber-768' | 'Kyber-1024',
    dilithium: 'Dilithium3' as 'Dilithium2' | 'Dilithium3' | 'Dilithium5'
  });

  const keyMetrics = getKeyMetrics();

  const getSecurityLevelColor = (level: string) => {
    switch (level) {
      case 'high': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-orange-600 bg-orange-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSecurityIcon = (level: string) => {
    switch (level) {
      case 'high': return <CheckCircle className="h-5 w-5" />;
      case 'medium': return <AlertTriangle className="h-5 w-5" />;
      case 'low': return <Clock className="h-5 w-5" />;
      case 'critical': return <XCircle className="h-5 w-5" />;
      default: return <Shield className="h-5 w-5" />;
    }
  };

  const getAlgorithmInfo = (algorithm: string) => {
    const info: Record<string, { level: string; description: string; keySize: string }> = {
      'Kyber-512': { level: 'NIST Level 1', description: 'Equivalente a AES-128', keySize: '800 bytes' },
      'Kyber-768': { level: 'NIST Level 3', description: 'Equivalente a AES-192', keySize: '1,184 bytes' },
      'Kyber-1024': { level: 'NIST Level 5', description: 'Equivalente a AES-256', keySize: '1,568 bytes' },
      'Dilithium2': { level: 'NIST Level 2', description: 'Assinatura compacta', keySize: '1,312 bytes' },
      'Dilithium3': { level: 'NIST Level 3', description: 'Balanceado', keySize: '1,952 bytes' },
      'Dilithium5': { level: 'NIST Level 5', description: 'Máxima segurança', keySize: '2,592 bytes' }
    };
    return info[algorithm] || { level: 'Desconhecido', description: '', keySize: '' };
  };

  const handleGenerateKeys = async () => {
    await generateKeys(selectedAlgorithms.kyber, selectedAlgorithms.dilithium);
  };

  const handleBackupKeys = async () => {
    const backup = await backupKeys();
    if (backup) {
      setBackupData(backup);
      setShowBackupModal(true);
    }
  };

  const handleRestoreKeys = async () => {
    if (!restoreData.trim()) {
      toast.error('Insira os dados de backup');
      return;
    }

    const success = await restoreKeys(restoreData);
    if (success) {
      setShowRestoreModal(false);
      setRestoreData('');
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado para a área de transferência`);
    } catch (error) {
      toast.error('Erro ao copiar para a área de transferência');
    }
  };

  const downloadBackup = () => {
    if (!backupData) return;
    
    const blob = new Blob([backupData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pqc-backup-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Backup baixado com sucesso');
  };

  const maskKey = (key: string, show: boolean) => {
    if (show) return key;
    return key.substring(0, 8) + '•'.repeat(Math.max(0, key.length - 16)) + key.substring(key.length - 8);
  };

  if (!user) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Lock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Faça login para acessar o gerenciador de chaves PQC</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Status de Segurança */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Status de Segurança PQC
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getSecurityIcon(securityStatus.level)}
              <div>
                <p className="font-medium">{securityStatus.message}</p>
                <p className="text-sm text-gray-500">
                  Última verificação: {formatDistanceToNow(new Date(securityStatus.lastCheck), { 
                    addSuffix: true, 
                    locale: ptBR 
                  })}
                </p>
              </div>
            </div>
            <Badge className={getSecurityLevelColor(securityStatus.level)}>
              {securityStatus.level.toUpperCase()}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Métricas das Chaves */}
      {hasValidKeys() && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium">Força da Criptografia</p>
                  <p className="text-2xl font-bold text-blue-600">{keyMetrics.strength}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <FileKey className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium">Algoritmo</p>
                  <p className="text-lg font-semibold text-green-600">{keyMetrics.algorithm}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="text-sm font-medium">Idade das Chaves</p>
                  <p className="text-lg font-semibold text-orange-600">{keyMetrics.age} dias</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Gerenciamento de Chaves */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Chaves Criptográficas
          </CardTitle>
          <CardDescription>
            Gerencie suas chaves de criptografia pós-quântica Kyber e Dilithium
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!hasValidKeys() ? (
            <div className="text-center py-8">
              <Key className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma chave encontrada</h3>
              <p className="text-gray-500 mb-6">Gere suas chaves PQC para começar a usar criptografia pós-quântica</p>
              
              {/* Seleção de Algoritmos */}
              <div className="max-w-md mx-auto space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Algoritmo Kyber</label>
                  <select 
                    value={selectedAlgorithms.kyber}
                    onChange={(e) => setSelectedAlgorithms(prev => ({ 
                      ...prev, 
                      kyber: e.target.value as any 
                    }))}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="Kyber-512">Kyber-512 (Rápido)</option>
                    <option value="Kyber-768">Kyber-768 (Recomendado)</option>
                    <option value="Kyber-1024">Kyber-1024 (Máxima Segurança)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Algoritmo Dilithium</label>
                  <select 
                    value={selectedAlgorithms.dilithium}
                    onChange={(e) => setSelectedAlgorithms(prev => ({ 
                      ...prev, 
                      dilithium: e.target.value as any 
                    }))}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="Dilithium2">Dilithium2 (Compacto)</option>
                    <option value="Dilithium3">Dilithium3 (Recomendado)</option>
                    <option value="Dilithium5">Dilithium5 (Máxima Segurança)</option>
                  </select>
                </div>
              </div>
              
              <Button 
                onClick={handleGenerateKeys}
                disabled={isGeneratingKeys}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isGeneratingKeys ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Key className="h-4 w-4 mr-2" />
                )}
                {isGeneratingKeys ? 'Gerando Chaves...' : 'Gerar Chaves PQC'}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Chave Kyber */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Lock className="h-5 w-5 text-blue-600" />
                    <h4 className="font-semibold">Chave Kyber (Criptografia)</h4>
                    <Badge variant="outline">{kyberKeys?.algorithm}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPrivateKeys(!showPrivateKeys)}
                    >
                      {showPrivateKeys ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Chave Pública</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-gray-50 rounded text-xs font-mono break-all">
                        {kyberKeys?.publicKey.substring(0, 64)}...
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(kyberKeys?.publicKey || '', 'Chave pública Kyber')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Chave Privada</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-gray-50 rounded text-xs font-mono break-all">
                        {maskKey(kyberKeys?.privateKey || '', showPrivateKeys)}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(kyberKeys?.privateKey || '', 'Chave privada Kyber')}
                        disabled={!showPrivateKeys}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-500">
                    <p>Criada: {kyberKeys?.createdAt ? formatDistanceToNow(new Date(kyberKeys.createdAt), { addSuffix: true, locale: ptBR }) : 'Desconhecido'}</p>
                    <p>Nível: {getAlgorithmInfo(kyberKeys?.algorithm || '').level}</p>
                  </div>
                </div>
              </div>

              {/* Chave Dilithium */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileKey className="h-5 w-5 text-green-600" />
                    <h4 className="font-semibold">Chave Dilithium (Assinatura)</h4>
                    <Badge variant="outline">{dilithiumKeys?.algorithm}</Badge>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Chave Pública</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-gray-50 rounded text-xs font-mono break-all">
                        {dilithiumKeys?.publicKey.substring(0, 64)}...
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(dilithiumKeys?.publicKey || '', 'Chave pública Dilithium')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Chave Privada</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-gray-50 rounded text-xs font-mono break-all">
                        {maskKey(dilithiumKeys?.privateKey || '', showPrivateKeys)}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(dilithiumKeys?.privateKey || '', 'Chave privada Dilithium')}
                        disabled={!showPrivateKeys}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-500">
                    <p>Criada: {dilithiumKeys?.createdAt ? formatDistanceToNow(new Date(dilithiumKeys.createdAt), { addSuffix: true, locale: ptBR }) : 'Desconhecido'}</p>
                    <p>Nível: {getAlgorithmInfo(dilithiumKeys?.algorithm || '').level}</p>
                  </div>
                </div>
              </div>

              {/* Ações */}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={rotateKeys}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Rotacionar Chaves
                </Button>
                
                <Button
                  onClick={handleBackupKeys}
                  disabled={isBackingUp}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  {isBackingUp ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  {isBackingUp ? 'Criando Backup...' : 'Backup'}
                </Button>
                
                <Button
                  onClick={() => setShowRestoreModal(true)}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Restaurar
                </Button>
                
                <Button
                  onClick={() => setShowKeyDetails(true)}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Info className="h-4 w-4" />
                  Detalhes
                </Button>
                
                <Button
                  onClick={removeKeys}
                  variant="outline"
                  className="flex items-center gap-2 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                  Remover
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Backup */}
      <Modal
        isOpen={showBackupModal}
        onClose={() => setShowBackupModal(false)}
        title="Backup das Chaves PQC"
        size="lg"
      >
        <div className="p-6 space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <h4 className="font-semibold text-yellow-800">Importante</h4>
            </div>
            <p className="text-sm text-yellow-700">
              Mantenha este backup em local seguro. Qualquer pessoa com acesso a estes dados 
              poderá descriptografar suas informações.
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Dados de Backup</label>
            <textarea
              value={backupData}
              readOnly
              className="w-full h-32 p-3 border border-gray-300 rounded-md font-mono text-xs"
              placeholder="Dados de backup aparecerão aqui..."
            />
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={() => copyToClipboard(backupData, 'Backup')}
              className="flex items-center gap-2"
            >
              <Copy className="h-4 w-4" />
              Copiar
            </Button>
            
            <Button
              onClick={downloadBackup}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Baixar Arquivo
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Restauração */}
      <Modal
        isOpen={showRestoreModal}
        onClose={() => setShowRestoreModal(false)}
        title="Restaurar Chaves PQC"
        size="lg"
      >
        <div className="p-6 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <h4 className="font-semibold text-red-800">Atenção</h4>
            </div>
            <p className="text-sm text-red-700">
              Esta ação substituirá suas chaves atuais. Certifique-se de ter um backup 
              das chaves atuais antes de prosseguir.
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Dados de Backup</label>
            <textarea
              value={restoreData}
              onChange={(e) => setRestoreData(e.target.value)}
              className="w-full h-32 p-3 border border-gray-300 rounded-md font-mono text-xs"
              placeholder="Cole aqui os dados de backup..."
            />
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={handleRestoreKeys}
              disabled={isRestoring || !restoreData.trim()}
              className="flex items-center gap-2"
            >
              {isRestoring ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {isRestoring ? 'Restaurando...' : 'Restaurar'}
            </Button>
            
            <Button
              onClick={() => setShowRestoreModal(false)}
              variant="outline"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Detalhes */}
      <Modal
        isOpen={showKeyDetails}
        onClose={() => setShowKeyDetails(false)}
        title="Detalhes das Chaves PQC"
        size="lg"
      >
        <div className="p-6 space-y-6">
          {kyberKeys && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Lock className="h-5 w-5 text-blue-600" />
                Kyber - {kyberKeys.algorithm}
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Nível de Segurança:</span>
                  <p>{getAlgorithmInfo(dilithiumKeys.algorithm).level}</p>
                </div>
                <div>
                  <span className="font-medium">Descrição:</span>
                  <p>{getAlgorithmInfo(dilithiumKeys.algorithm).description}</p>
                </div>
                <div>
                  <span className="font-medium">Tamanho da Chave:</span>
                  <p>{getAlgorithmInfo(dilithiumKeys.algorithm).keySize}</p>
                </div>
                <div>
                  <span className="font-medium">Criada em:</span>
                  <p>{new Date(dilithiumKeys.createdAt).toLocaleString('pt-BR')}</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="border-t pt-4">
            <h4 className="font-semibold mb-3">Chaves Públicas</h4>
            <div className="space-y-3">
              {kyberKeys && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chave Pública Kyber
                  </label>
                  <div className="flex gap-2">
                    <textarea
                      value={maskKey(kyberKeys.publicKey, showPrivateKeys)}
                      readOnly
                      className="flex-1 p-2 border border-gray-300 rounded text-xs font-mono"
                      rows={3}
                    />
                    <Button
                      onClick={() => copyToClipboard(kyberKeys.publicKey, 'Chave pública Kyber')}
                      variant="outline"
                      size="sm"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {dilithiumKeys && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chave Pública Dilithium
                  </label>
                  <div className="flex gap-2">
                    <textarea
                      value={maskKey(dilithiumKeys.publicKey, showPrivateKeys)}
                      readOnly
                      className="flex-1 p-2 border border-gray-300 rounded text-xs font-mono"
                      rows={3}
                    />
                    <Button
                      onClick={() => copyToClipboard(dilithiumKeys.publicKey, 'Chave pública Dilithium')}
                      variant="outline"
                      size="sm"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PQCKeyManager;